/**
 * Outlet Selectors
 * Selectors for accessing outlet state
 */

import { createSelector } from '@reduxjs/toolkit';
import { AccessControlCode, FULL_ACCESS_USER_ROLES } from '@shared/constants';
import { Outlet as SharedOutlet, UserRole } from '@shared/types';
import type { RootState } from '..';
import { Outlet, OutletState } from '../../types/outlet';

const selectOutletState = (state: RootState): OutletState => state.outlets;

const selectAuthOutlet = (state: RootState): SharedOutlet | null =>
  state.auth.user?.outlet ?? null;

const selectAuthRole = (state: RootState) => state.auth.user?.role ?? null;

const selectAccessControlCodes = (state: RootState) =>
  state.auth.accessControlCodes;

const mapSharedOutletToRendererOutlet = (outlet: SharedOutlet): Outlet => ({
  id: outlet.id,
  company_id: outlet.company_id,
  name: outlet.name,
  address: outlet.address,
  tin_number: outlet.tin_number,
  phone: outlet.phone,
  currency: outlet.currency ?? null,
  currency_prefix: outlet.currency_prefix ?? null,
  receipt_footnote: outlet.receipt_footnote,
  day_open: outlet.day_open,
  business_type: outlet.business_type,
  kot_tracking_enabled: outlet.kot_tracking_enabled,
  is_kds_enabled: outlet.is_kds_enabled,
  created_at: outlet.created_at,
  updated_at: outlet.updated_at,
});

export const selectSelectedOutletId = createSelector(
  [selectOutletState],
  (outlets) => outlets.selectedOutletId,
);

export const selectAuthOutletId = createSelector(
  [selectAuthOutlet],
  (outlet) => outlet?.id ?? null,
);

export const selectCanUseCompanyScope = createSelector(
  [selectAuthRole, selectAccessControlCodes],
  (role, accessControlCodes) =>
    Boolean(
      (role && FULL_ACCESS_USER_ROLES.includes(role as UserRole)) ||
      accessControlCodes.includes(
        AccessControlCode.DASHBOARD_MULTIPLE_OUTLETS_ACCESS,
      ),
    ),
);

const selectStoredOutlets = createSelector(
  [selectOutletState],
  (outlets) => outlets.outlets,
);

export const selectAllOutlets = createSelector(
  [selectStoredOutlets, selectCanUseCompanyScope, selectAuthOutlet],
  (outlets, canUseCompanyScope, authOutlet) => {
    if (canUseCompanyScope) {
      return outlets;
    }

    if (!authOutlet) {
      return [];
    }

    const outlet = outlets.find((item) => item.id === authOutlet.id);

    return outlet ? [outlet] : [mapSharedOutletToRendererOutlet(authOutlet)];
  },
);

export const selectEffectiveOutletId = createSelector(
  [selectSelectedOutletId, selectAuthOutletId, selectCanUseCompanyScope],
  (selectedId, authOutletId, canUseCompanyScope) => {
    if (!canUseCompanyScope) {
      return authOutletId || null;
    }

    return selectedId || null;
  },
);

export const selectSelectedOutlet = createSelector(
  [selectAllOutlets, selectSelectedOutletId],
  (outlets, selectedId) =>
    selectedId ? outlets.find((o) => o.id === selectedId) : null,
);

export const selectEffectiveOutlet = createSelector(
  [
    selectAllOutlets,
    selectSelectedOutletId,
    selectAuthOutletId,
    selectCanUseCompanyScope,
    selectAuthOutlet,
  ],
  (
    outlets,
    selectedOutletId,
    authOutletId,
    canUseCompanyScope,
    authOutlet,
  ): Outlet | null => {
    const outletId = canUseCompanyScope
      ? selectedOutletId || null
      : authOutletId || null;
    if (!outletId) return null;

    const selected = outlets.find((outlet) => outlet.id === outletId);
    if (selected) return selected;

    if (authOutlet?.id === outletId) {
      return mapSharedOutletToRendererOutlet(authOutlet);
    }

    return null;
  },
);

export const selectOutletLimits = createSelector(
  [selectOutletState],
  (outlets) => outlets.limits,
);

export const selectCanCreateOutlet = createSelector(
  [selectOutletLimits],
  (limits) => limits?.canCreateOutlet ?? true,
);

export const selectOutletById = (outletId: string) =>
  createSelector([selectAllOutlets], (outlets) =>
    outlets.find((o) => o.id === outletId),
  );

export const selectOutletLoading = createSelector(
  [selectOutletState],
  (outlets) => outlets.isLoading,
);

export const selectOutletError = createSelector(
  [selectOutletState],
  (outlets) => outlets.error,
);

export const selectOutletCount = createSelector(
  [selectAllOutlets],
  (outlets) => outlets.length,
);

export const selectOutletSelectorValue = createSelector(
  [selectSelectedOutletId, selectAuthOutletId, selectCanUseCompanyScope],
  (selectedOutletId, authOutletId, canUseCompanyScope) => {
    if (!canUseCompanyScope) {
      return authOutletId || '';
    }

    return selectedOutletId || '';
  },
);
